import React, { useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash, faUser, faShieldExclamation, faEye, faSpinner } from "@fortawesome/pro-solid-svg-icons"; // Added faSpinner
import { faKeySkeleton, faCircle } from "@fortawesome/pro-regular-svg-icons";
import { Divider } from "primereact/divider";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context";
import JSEncrypt from "jsencrypt";
import { EsubeApi, Platform } from "../../../../services/authentication/api";
import { Configuration } from "../../../../services/authentication/configuration";
import { login, setRememberMe, setRememberMeUserLogin } from "../../../../stores/auth";
import { Toast } from "primereact/toast";
import { Checkbox } from 'primereact/checkbox';

const MetKey = `-----BEGIN PUBLIC KEY-----
MIIBITANBgkqhkiG9w0BAQEFAAOCAQ4AMIIBCQKCAQB3prLK5rWvXhOfC8JyMImg
NWH1XaY8+pQGs5FVy0dAPcogNi7FJKQvjNOx2nNl4ESU1ShT3TNe69L8x45MR2zg
OGbgN1NXdCCYu7bsJlQUxWuu1jEqZVl5PKgz8JSrkk47kRUFHw2Ogb50nYhR9X0q
LaU+UZshu5UG/ut/gaSS8TStrHwGifYsVRJIytSldEWlUWtuh19dc7RHVhwd3nd5
lBqqJC3cy2va2rRjC0f/UwHCANfvOPMT1Kfe+9ygihQ8l9B199+kgzy1Z/loBRsr
mVttnyKF9qvjzXbk5ZaFznyAFOu0pdLf3Wqp0X7YPFV7K2tqBoLJACUpH2NSUDej
AgMBAAE=
-----END PUBLIC KEY-----`;

export default function UserLoginForm() {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(MetKey);

  const esubeApi = new EsubeApi(new Configuration({ basePath: process.env.REACT_APP_AUTHENTICATION_API_BASE_PATH }));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useRef(null);
  const contextVal = useContext(AuthContext);
  const rememberMe = useSelector((state) => state.auth?.rememberMe);
  const rememberMeUserLogin = useSelector((state) => state.auth?.rememberMeUserLogin);

  const [userLoginInputs, setUserLoginInputs] = useState({
    customerNumber: rememberMeUserLogin?.customerNumber || "",
    password: rememberMeUserLogin?.password || ""
  });
  const [rememberMeCheck, setRememberMeCheck] = useState(rememberMe);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Added isLoading state

  useEffect(() => {
    if (rememberMe) {
      setUserLoginInputs({
        customerNumber: rememberMeUserLogin?.customerNumber || "",
        password: rememberMeUserLogin?.password || ""
      });
      setRememberMeCheck(rememberMe);
    }
  }, [rememberMe, rememberMeUserLogin]);

  const handleRememberMeChange = (checked) => {
    setRememberMeCheck(checked);

    if (!checked) {
      dispatch(setRememberMe(false));
      dispatch(setRememberMeUserLogin(null));
    }
  };

  const handleForgotPassword = () => {
    try {
      window.open('https://esube.osmanlimenkul.com.tr/sifremi-unuttum', '_blank');
    } catch (error) {
      console.error("Error handling forgot password:", error);
    }
  };

  const userLoginHandler = (e) => {
    e?.preventDefault();
    setIsLoading(true); // Set loading to true
    try {
      let userData = userLoginInputs;
      const customerNumber = parseInt(userData.customerNumber, 10);

      if (!customerNumber || Number.isNaN(customerNumber)) {
        throw new Error("Müşteri numarası girmelisiniz.");
      }

      const encryptedPassword = encrypt.encrypt(userData.password);

      if (encryptedPassword === false) {
        // JSEncrypt returns false if encryption fails (e.g., input too long)
        throw new Error("Şifreleme sırasında bir hata oluştu.");
      }

      esubeApi.apiEsubeBeginLoginPost(Platform.Web, { CustomerNumber: customerNumber, Password: encryptedPassword })
        .then((res) => {

          if (res.data.Status !== "Error") {
            dispatch(setRememberMe(rememberMeCheck));

            if (rememberMeCheck) {
              dispatch(setRememberMeUserLogin({
                customerNumber: userData.customerNumber,
                password: userData.password,
              }));
            } else {
              dispatch(setRememberMeUserLogin(null));
            }

            // Her kullanıcı için farklı gelebilen Typeof2FA değerine göre doğru doğrulama ekranını seç
            const isOtp = (res.data.Data.Typeof2FA || "").toLowerCase() === "otp";

            contextVal.setAuthState({
              ...contextVal.authState,
              loginView: false,
              smsVerificationView: !isOtp,
              twoFactorVerification: isOtp,
              userLoginInputs: {
                ...userData,
                mfaTraceId: res.data.Data.MFATraceId || ""
              },
              customerNumber: customerNumber
            });

            toast.current.show({
              severity: "success",
              summary: "Giriş Başarılı",
              detail: isOtp
                ? "Doğrulama kodu gönderildi. Lütfen doğrulama uygulamanızdaki kodu giriniz."
                : "Doğrulama kodu gönderildi. Lütfen SMS kodunu giriniz.",
              closable: true,
              life: 3000,
            });
          } else {
            const errorCodes = ["0x7D3D2F", "0xBC3E1F", "0xNT3A2B"];
            if (errorCodes.some(code => res.data.Error.includes(code))) {
              toast.current.show({
                severity: "error",
                summary: "Giriş Başarısız",
                detail: "Hesap bilgilerinizi kontrol ediniz. " + res.data.Error,
                closable: true,
                life: 3000,
              });
            } else {
              toast.current.show({
                severity: "error",
                summary: "Giriş Hatası",
                detail: res.data.Error,
                closable: true,
                life: 3000,
              });
            }
          }
        })
        .catch((apiError) => {
          console.error("Login API error:", apiError);
          toast.current.show({
            severity: "error",
            summary: "API Hatası",
            detail: "Giriş sırasında sunucuyla iletişim kurulamadı veya bir API hatası oluştu.",
            closable: true,
            life: 3000,
          });
        })
        .finally(() => {
          setIsLoading(false); // Set loading to false regardless of success or failure
        });
    } catch (error) {
      console.error("User login handler error:", error);
      setIsLoading(false); // Set loading to false on general error
      toast.current.show({
        severity: "error",
        summary: "İstemci Hatası",
        detail: error.message || "Giriş işlemi sırasında beklenmedik bir hata oluştu.",
        closable: true,
        life: 3000,
      });
    }
  };

  const handleMouseDownPassword = () => {
    setIsShowPassword(true);
  };

  const handleMouseUpPassword = () => {
    setIsShowPassword(false);
  };

  return (
    <>
      <Toast ref={toast} position="bottom-right" />
      <Form onSubmit={userLoginHandler}>
        <Row className="m-t-40">
          <Form.Group className="mb-3" controlId="ControlInput1">
            <InputGroup className="user-mail-input-group w-100">
              <InputGroup.Text id="inputGroup-sizing-default" className="p-5">
                <FontAwesomeIcon icon={faUser} className="userLoginUserIcon" />
              </InputGroup.Text>
              <Form.Control
                type="number"
                className="customRegisterInput"
                placeholder="Müşteri Numarası"
                value={userLoginInputs.customerNumber}
                onChange={(e) => { setUserLoginInputs({ ...userLoginInputs, customerNumber: e.target.value }) }}
              />
            </InputGroup>
          </Form.Group>
        </Row>
        <Row>
          <Form.Group className="mb-3" controlId="ControlInput7">
            <InputGroup className="user-password-input-group w-100">
              <InputGroup.Text id="inputGroup-sizing-default" className="p-5">
                <FontAwesomeIcon icon={faKeySkeleton} className="userLoginKeyIcon" />
              </InputGroup.Text>
              <Form.Control
                className="userLoginPassword customRegisterInput"
                value={userLoginInputs.password}
                onChange={(e) => { setUserLoginInputs({ ...userLoginInputs, password: e.target.value }) }}
                type={isShowPassword ? "text" : "password"}
                aria-label="Default"
                aria-describedby="inputGroup-sizing-default"
                placeholder="Şifre"
                name="password1"
              />
              <InputGroup.Text
                id="inputGroup-sizing-default"
                className="userLoginEyeIcon"
                onMouseDown={handleMouseDownPassword}
                onMouseUp={handleMouseUpPassword}
                onMouseLeave={handleMouseUpPassword}
              >
                <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </Row>
        <Row className="remember-forgotpass-row">
          <Col xs="6">
            <Checkbox
              inputId="rememberMe"
              checked={rememberMeCheck}
              onChange={(e) => handleRememberMeChange(e.checked)}
              className="rememberMeCheckbox"
            />
            <label htmlFor="rememberMe" className="rememberMeCheck">Beni Hatırla</label>
          </Col>
          <Col xs="6" className="userForgotPassword">
            <button type="button" className="forgotPasswordButton" onClick={handleForgotPassword}>
              Şifrenizi mi unuttunuz ?
            </button>
          </Col>
        </Row>
        <Row style={{ display: "inline" }} className="m-0 ">
          <Button
            type="submit"
            slot="end"
            variant="dark"
            className="loginBtn m-t-40 m-b-15"
            disabled={isLoading} // Disable button when loading
          >
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "GİRİŞ YAP"}
          </Button>
        </Row>
        <Row style={{ display: "inline" }} className="m-0">
          <Button
            slot="end"
            variant="dark"
            className="registerBtn"
            onClick={() => {
              navigate("/auth/register");
            }}
          >
            DEMO HESABI AÇ
          </Button>
        </Row>
        <Divider align="center" className="loginDivider">
          <FontAwesomeIcon icon={faCircle} className="loginDividerIcon" />
        </Divider>
        <Row className="m-0 loginFooterInfo">
          <label className="loginFooterInfoLabel">Osmanlı Yatırım müşterisi olarak ilk giriş öncesinde e-şube hesabım / veri dağıtım talep ekranından HisseLAB ürün talebini oluşturmanız gerekmektedir.</label>
          <label className="shieldExclamationDiv">
            <FontAwesomeIcon icon={faShieldExclamation} className="shieldExclamationIcon" />
          </label>
        </Row>
      </Form>
    </>
  );
}
